<template>
  <v-row no-gutters class="fill-height flex-column" style="overflow-y: auto; overflow-x: hidden;">
    <v-col cols="12" class="pr-6">
      <card-title-row :straightTitle="additionalModuleName" class="py-6" />
      <sidebar-form
        ref="formDem"
        :is-valid.sync="isDemFormValid"
        is-editing-on
        :inputs="demInputs"
        v-model="demPayload"
        hide-rules-icon
      >
        <template #geometry="data">
          <data-input ref="geometry-input" v-model="demPayload[data.name]" v-bind="data" />
          <line-summary :geometry="currentLineGeometry">
            <template #length-append>
              <v-tooltip bottom color="font">
                <template #activator="{ on }">
                  <v-icon
                    @click="zoomToGeometry"
                    color="rgba(0,0,0,.7)"
                    size="19px"
                    :disabled="isStickyButtonsLoading"
                    v-on="on"
                  >
                    mdi-magnify
                  </v-icon>
                </template>
                {{ $i18n.t('sidebar.fresnelZone.zoomToFeatureTooltip') }}
              </v-tooltip>
              <v-tooltip bottom color="font">
                <template #activator="{ on }">
                  <v-icon
                    @click="deleteGeometry"
                    color="error"
                    size="19px"
                    :disabled="isStickyButtonsLoading"
                    v-on="on"
                  >
                    mdi-close
                  </v-icon>
                </template>
                {{ $i18n.t('sidebar.fresnelZone.deleteFeatureTooltip') }}
              </v-tooltip>
            </template>
          </line-summary>
        </template>
        <template #interval="data">
          <v-row no-gutters class="flex-nowrap" align="center">
            <v-col cols="auto">
              <data-input v-model="demPayload[data.name]" v-bind="data" :disabled="isStickyButtonsLoading" />
            </v-col>
            <v-col cols="auto" class="pb-6 pl-3">
              m
            </v-col>
          </v-row>
        </template>
        <template #useNmpt="data">
          <v-checkbox v-model="useNmpt">
            <template #label>
              <div class="text-body-2 text-left label-dark">
                {{ data.label }}
              </div>
            </template>
          </v-checkbox>
        </template>
      </sidebar-form>
      <template v-if="isDemDone">
        <v-row no-gutters>
          <v-col cols="12">
            <sidebar-form
              ref="formFresnelZone"
              :is-valid.sync="isFresnelZoneFormValid"
              is-editing-on
              :inputs="fresnelZoneInputs"
              v-model="fresnelZonePayload"
              hide-rules-icon
            >
              <template #fresnelZoneVisible="data">
                <v-checkbox v-model="fresnelZonePayload[data.name]" v-bind="data">
                  <template #label>
                    <div class="text-body-2 text-left label-dark">
                      {{ $i18n.t(data.translationPath) }}
                    </div>
                  </template>
                </v-checkbox>
              </template>
            </sidebar-form>
          </v-col>
        </v-row>
        <v-row no-gutters>
          <v-col cols="12">
            <nmt-sidebar-profile
              @showDialog="showDemChartDialog"
              :heights="demHeights"
              :length="demLength"
              :interval="demInterval"
              :fresnelZonePoints="JSON.parse(JSON.stringify(fresnelZonePoints))"
              @drawMarker="drawDemMarker"
              :title-path="profileChartDialogTitle"
            />
          </v-col>
          <nmt-sidebar-profile-chart-dialog
            v-if="isDemChartDialogLoaded"
            :is-visible.sync="isDemChartDialogVisible"
            :heights="demHeights"
            :length="demLength"
            :interval="demInterval"
            :fresnelZonePoints="JSON.parse(JSON.stringify(fresnelZonePoints))"
            :title-path="profileChartDialogTitle"
          />
        </v-row>
      </template>
    </v-col>
  </v-row>
</template>
<script>
import { call, get, sync } from 'vuex-pathify';
import { debounce } from 'lodash';

import CardTitleRow from '@/components/CardTitleRow';
import DataInput from '@/components/DataInput';
import LineSummary from '@/components/LineSummary';
import SidebarForm from '@/components/SidebarForm';

import NmtSidebarProfile from '@/components/NmtSidebarProfile';
import NmtSidebarProfileChartDialog from '@/components/NmtSidebarProfileChartDialog';

export default {
  name: 'FresnelZone',
  components: { CardTitleRow, DataInput, LineSummary, SidebarForm, NmtSidebarProfile, NmtSidebarProfileChartDialog },
  computed: {
    additionalModuleName: get('admin/additionalModules.FRESNEL_AREA.verbose_name'),
    nmtFileName: get('admin/modulesMapping@fresnel_area.nmt_file_name'),
    nmptFileName: get('admin/modulesMapping@fresnel_area.nmpt_file_name'),
    rasterFileName() {
      return this.useNmpt ? this.nmptFileName : this.nmtFileName;
    },
    demInputs() {
      return [
        {
          name: 'geometry',
          dataType: {
            name: 'geometry',
          },
          geometryType: 'LineString',
          maxNumberOfVertices: 2,
          buttonProps: {
            translationPath: 'sidebar.fresnelZone.drawLine',
          },
          subtitleTranslationPath: 'sidebar.fresnelZone.drawLineSubtitle',
          hasNameColumn: false,
          cols: '12',
          hasDivider: true,
          disabled: this.currentLineGeometry ? true : false,
        },
        {
          name: 'interval',
          translationPath: 'sidebar.fresnelZone.measruementEvery',
          dataType: {
            name: 'integer',
          },
          nameCols: '4',
          cols: '4',
          justifyClass: 'start',
          rules: [v => !!v || v == '0' || 'rules.required'],
          allowNegative: false,
          minValue: 1,
        },
        {
          name: 'useNmpt',
          cols: '10',
          hasNameColumn: false,
          dataType: { name: 'checkbox' },
          label: this.$i18n.t('sidebar.fresnelZone.useNmpt'),
        },
      ];
    },
    fresnelZoneInputs() {
      return [
        {
          name: 'fresnelZoneVisible',
          translationPath: 'sidebar.fresnelZone.fresnelZoneVisible',
          hideDetails: true,
          dataType: {
            name: 'checkbox',
          },
          hasNameColumn: false,
          cols: '12',
        },
        ...(this.fresnelZonePayload['fresnelZoneVisible']
          ? [
              {
                name: 'frequency',
                translationPath: 'sidebar.fresnelZone.frequency',
                dataType: {
                  name: 'integer',
                },
                nameCols: '5',
                cols: '6',
                rules: [v => !!v || v == '0' || 'rules.required'],
                allowNegative: false,
                minValue: 1,
              },
              {
                name: 'startHeight',
                translationPath: 'sidebar.fresnelZone.startHeight',
                dataType: {
                  name: 'integer',
                },
                nameCols: '5',
                cols: '6',
                rules: [v => !!v || v == '0' || 'rules.required'],
                allowNegative: false,
                minValue: 1,
              },
              {
                name: 'endHeight',
                translationPath: 'sidebar.fresnelZone.endHeight',
                dataType: {
                  name: 'integer',
                },
                nameCols: '5',
                cols: '6',
                rules: [v => !!v || v == '0' || 'rules.required'],
                allowNegative: false,
                minValue: 1,
              },
            ]
          : []),
      ];
    },
    profileChartDialogTitle() {
      return this.fresnelZonePoints?.length
        ? 'sidebar.fresnelZone.chartTitleFresnelZone'
        : 'sidebar.fresnelZone.chartTitleDem';
    },

    isStickyButtonsLoading: sync('sidebar/isStickyButtonsLoading'),
    isStickyButtonsValid: sync('sidebar/isStickyButtonsValid'),
    isStickyButtonsVisible: sync('sidebar/isStickyButtonsVisible'),
    stickyButtons: sync('sidebar/stickyButtons'),
  },
  data: () => ({
    currentLineGeometry: null,
    demHeights: [],
    demLength: null,
    demInterval: null,
    isDemDone: false,
    isDemChartDialogLoaded: false,
    isDemChartDialogVisible: false,
    isDemFormValid: false,
    isFresnelZoneFormValid: false,
    demPayload: {
      geometry: undefined,
      interval: null,
    },
    fresnelZonePayload: {
      fresnelZoneVisible: false,
      frequency: null,
      startHeight: null,
      endHeight: null,
    },
    fresnelZonePoints: [],
    useNmpt: false,
  }),
  methods: {
    getProfile: call('nmt/getFresnelProfile'),
    async generateDemChart() {
      this.isDemDone = false;
      this.isStickyButtonsLoading = true;
      try {
        const r = await this.getProfile({
          payload: { interval: this.demPayload.interval, line: this.currentLineGeometry },
          fileName: this.rasterFileName,
        });
        if (r.data.data.heights?.length > 0) {
          this.demHeights = r.data.data.heights;
          this.demLength = r.data.data.statistics.length?.toFixed(2);
          this.demInterval = JSON.parse(JSON.stringify(this.demPayload.interval));
          this.isStickyButtonsLoading = false;
          this.isDemDone = true;
          this.stickyButtons = [
            {
              action: 'fresnelZoneGenerateDemChart',
              translationPath: 'button.generateChart',
            },
            {
              action: 'fresnelZoneReset',
              cancel: true,
              translationPath: 'button.reset',
            },
          ];
        } else {
          throw new Error(this.$i18n.t('errors.profileNoResults'));
        }
      } catch {
        this.isStickyButtonsLoading = false;
      }
    },
    toggleFresnelZone(value, params, fresnelInterval = this.demInterval) {
      if (value) {
        const { frequency, startHeight, endHeight } = params;
        let isLastPoint = false;
        const point1 = {
          x: 0,
          y: +this.demHeights[0] + startHeight,
        };
        const point2 = {
          x: +this.demLength,
          y: +this.demHeights.at(-1) + endHeight,
        };
        const linearAParam = (point2.y - point1.y) / (point2.x - point1.x);
        const linearBParam = point1.y - linearAParam * point1.x;
        const linearFunction = x => {
          return linearAParam * x + linearBParam;
        };
        const points = [];
        const lambda = 300000000 / frequency;
        let currentLength = 0;
        while (!isLastPoint && currentLength <= this.demLength) {
          if (currentLength >= this.demLength) isLastPoint = true;
          const d1 = currentLength;
          const d2 = this.demLength - currentLength;
          const r = Math.sqrt((lambda * d1 * d2) / (d1 + d2));
          points.push({
            x: d1,
            y: linearFunction(d1) + r,
          });
          if (d1 !== 0 && d2 !== 0) {
            points.push({
              x: d1,
              y: linearFunction(d1) - r,
            });
          }
          currentLength =
            currentLength + fresnelInterval > this.demLength ? this.demLength : currentLength + fresnelInterval;
        }
        this.fresnelZonePoints = points;
      } else {
        this.fresnelZonePoints = [];
      }
    },
    reset() {
      this.fresnelZonePoints = [];
      this.fresnelZonePayload = {
        fresnelZoneVisible: false,
        frequency: null,
        startHeight: null,
        endHeight: null,
      };
      this.isDemDone = false;
      this.demPayload.interval = null;
      this.stickyButtons = [
        {
          action: 'fresnelZoneGenerateDemChart',
          translationPath: 'button.generateChart',
        },
      ];
      this.$refs.formDem?.$refs.form?.resetValidation();
      this.$refs.formFresnelZone?.$refs.form?.resetValidation();
    },
    drawDemMarker(fraction) {
      this.$root.$emit('drawProfileMarker', this.currentLineGeometry, fraction);
    },
    showDemChartDialog() {
      this.isDemChartDialogLoaded = true;
      this.isDemChartDialogVisible = true;
    },
    deleteGeometry() {
      this.$root.$emit('toggleFresnelZoneLine', false);
      this.currentLineGeometry = null;
      this.reset();
    },
    zoomToGeometry() {
      this.$root.$emit('createGeomAndFitView', this.currentLineGeometry);
    },
  },
  watch: {
    'demPayload.geometry': {
      handler(nV) {
        if (!nV) return;
        this.currentLineGeometry = nV;
        this.$root.$emit('toggleFresnelZoneLine', nV);
        this.$refs[`geometry-input`]?.$refs['data-input']?.toggleOff();
      },
      deep: true,
    },
  },
  mounted() {
    this.toggleFresnelZone = debounce(this.toggleFresnelZone, 1000);
    this.$watch(
      vm => {
        return { isDemFormValid: vm.isDemFormValid, currentLineGeometry: vm.currentLineGeometry };
      },
      nV => {
        this.isStickyButtonsValid = nV.isDemFormValid && nV.currentLineGeometry;
      },
      {
        deep: true,
        immediate: true,
      }
    );
    this.$watch(
      vm => {
        const { fresnelZoneVisible, frequency, startHeight, endHeight } = vm.fresnelZonePayload;
        return { fresnelZoneVisible, frequency, startHeight, endHeight };
      },
      nV => {
        const { fresnelZoneVisible, frequency, startHeight, endHeight } = nV;
        this.toggleFresnelZone(fresnelZoneVisible && frequency && startHeight && endHeight ? true : false, nV);
      },
      {
        deep: true,
      }
    );

    this.stickyButtons = [
      {
        action: 'fresnelZoneGenerateDemChart',
        translationPath: 'button.generateChart',
      },
    ];
    this.isStickyButtonsVisible = true;
    this.isStickyButtonsLoading = false;

    this.$root.$off('fresnelZoneGenerateDemChart');
    this.$root.$on('fresnelZoneGenerateDemChart', this.generateDemChart);
    this.$root.$off('fresnelZoneReset');
    this.$root.$on('fresnelZoneReset', this.deleteGeometry);
  },
  beforeDestroy() {
    this.$root.$emit('toggleFresnelZoneLine', false);
    this.isStickyButtonsVisible = false;
    this.stickyButtons = [];
  },
};
</script>
<style lang="scss" scoped>
.label-dark {
  color: #202124;
}
::v-deep {
  .v-input--selection-controls {
    margin-top: 0;
    padding-top: 0;
  }
  .v-input--checkbox {
    padding-top: 0;
    margin-top: 0;
  }
}
</style>
